import React from 'react';
import { Box, Typography, Chip, Checkbox } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy as prismTheme } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { parseAsciiTable, type CodeProps } from './teachingChatUtils';

export type PreviewState = { open: boolean; src: string; alt: string };

export type CreateTeachingChatMarkdownComponentsOptions = {
  /** Slightly tighter pre block styling (streaming assistant bubble). */
  compactPre?: boolean;
};

export function createTeachingChatMarkdownComponents(
  setPreview: React.Dispatch<React.SetStateAction<PreviewState>>,
  options?: CreateTeachingChatMarkdownComponentsOptions,
) {
  const compactPre = options?.compactPre ?? false;
  const defaultPreSx = compactPre
    ? { backgroundColor: 'grey.100', p: 1, borderRadius: 1, overflow: 'auto' }
    : { backgroundColor: 'grey.100', p: 1.5, borderRadius: 1, overflow: 'auto', my: 1.5, lineHeight: 1.6 };

  return {
    h1: ({ children }: { children?: React.ReactNode }) => (
      <Typography variant="h4" component="h1" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>
        {children}
      </Typography>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
      <Typography variant="h5" component="h2" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>
        {children}
      </Typography>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
      <Typography variant="h6" component="h3" sx={{ mt: 1.5, mb: 0.75, fontWeight: 700 }}>
        {children}
      </Typography>
    ),
    h4: ({ children }: { children?: React.ReactNode }) => (
      <Typography variant="subtitle1" component="h4" sx={{ mt: 1.5, mb: 0.75, fontWeight: 600 }}>
        {children}
      </Typography>
    ),
    h5: ({ children }: { children?: React.ReactNode }) => (
      <Typography variant="subtitle2" component="h5" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
        {children}
      </Typography>
    ),
    h6: ({ children }: { children?: React.ReactNode }) => (
      <Typography variant="body1" component="h6" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
        {children}
      </Typography>
    ),
    p: ({ children }: { children?: React.ReactNode }) => (
      <Typography component="p" variant="body1" sx={{ mb: 1.25, lineHeight: 1.75 }}>
        {children}
      </Typography>
    ),
    ul: ({ children, ...props }: { children?: React.ReactNode; className?: string }) => {
      const isTaskList = props.className?.includes('contains-task-list');
      return (
        <Box component="ul" sx={{ pl: isTaskList ? 0 : 3, my: 1.25, listStyleType: isTaskList ? 'none' : 'disc' }}>
          {children}
        </Box>
      );
    },
    ol: ({ children }: { children?: React.ReactNode }) => (
      <Box component="ol" sx={{ pl: 3, my: 1.25, listStyleType: 'decimal' }}>
        {children}
      </Box>
    ),
    li: ({ children, ...props }: { children?: React.ReactNode; className?: string; checked?: boolean }) => {
      const isTaskItem = props.className?.includes('task-list-item');
      return (
        <Box
          component="li"
          sx={{
            my: 0.5,
            listStyle: isTaskItem ? 'none' : undefined,
            display: isTaskItem ? 'flex' : 'list-item',
            alignItems: 'flex-start',
          }}
        >
          {isTaskItem && props.checked !== undefined && (
            <Checkbox checked={props.checked} disabled sx={{ mt: 0.25, mr: 1, p: 0.5 }} size="small" />
          )}
          <Box sx={{ flex: 1 }}>{children}</Box>
        </Box>
      );
    },
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <Box
        component="blockquote"
        sx={{ borderLeft: '4px solid', borderColor: 'divider', pl: 2, my: 1.5, fontStyle: 'italic', color: 'text.secondary' }}
      >
        {children}
      </Box>
    ),
    hr: () => <Box component="hr" sx={{ my: 2, border: 'none', borderTop: '1px solid', borderColor: 'divider' }} />,
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <Typography
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: 'primary.main', textDecoration: 'underline', '&:hover': { textDecoration: 'none' } }}
      >
        {children}
      </Typography>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
      <Box component="strong" sx={{ fontWeight: 600 }}>
        {children}
      </Box>
    ),
    em: ({ children }: { children?: React.ReactNode }) => (
      <Box component="em" sx={{ fontStyle: 'italic' }}>
        {children}
      </Box>
    ),
    del: ({ children }: { children?: React.ReactNode }) => (
      <Box component="del" sx={{ textDecoration: 'line-through' }}>
        {children}
      </Box>
    ),
    table: ({ children }: { children?: React.ReactNode }) => (
      <Box sx={{ overflowX: 'auto', my: 2 }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', display: 'table', minWidth: '400px' }}>
          {children}
        </Box>
      </Box>
    ),
    thead: ({ children }: { children?: React.ReactNode }) => (
      <Box component="thead" sx={{ display: 'table-header-group', backgroundColor: 'grey.100' }}>
        {children}
      </Box>
    ),
    tbody: ({ children }: { children?: React.ReactNode }) => (
      <Box component="tbody" sx={{ display: 'table-row-group' }}>
        {children}
      </Box>
    ),
    tr: ({ children }: { children?: React.ReactNode }) => (
      <Box component="tr" sx={{ display: 'table-row' }}>
        {children}
      </Box>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
      <Box
        component="th"
        sx={{ border: '1px solid #e5e7eb', p: '8px', textAlign: 'left', fontWeight: 600, display: 'table-cell' }}
      >
        {children}
      </Box>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
      <Box component="td" sx={{ border: '1px solid #e5e7eb', p: '8px', textAlign: 'left', display: 'table-cell' }}>
        {children}
      </Box>
    ),
    img({ src, alt }: { src?: string; alt?: string }) {
      if (src && typeof src === 'string' && src.startsWith('data:image/')) {
        const label = (alt || 'Image').toString();
        return (
          <Chip
            icon={<ImageOutlinedIcon />}
            label={label}
            size="small"
            variant="outlined"
            onClick={() => setPreview({ open: true, src, alt: label })}
            sx={{ my: 0.5 }}
          />
        );
      }
      return <img src={src as string} alt={alt as string} style={{ maxWidth: '100%', height: 'auto' }} />;
    },
    dl: ({ children }: { children?: React.ReactNode }) => (
      <Box component="dl" sx={{ my: 1.5 }}>
        {children}
      </Box>
    ),
    dt: ({ children }: { children?: React.ReactNode }) => (
      <Box component="dt" sx={{ fontWeight: 600, mt: 1 }}>
        {children}
      </Box>
    ),
    dd: ({ children }: { children?: React.ReactNode }) => (
      <Box component="dd" sx={{ ml: 3, mb: 0.5 }}>
        {children}
      </Box>
    ),
    sup: ({ children }: { children?: React.ReactNode }) => (
      <Box component="sup" sx={{ fontSize: '0.75em', verticalAlign: 'super' }}>
        {children}
      </Box>
    ),
    sub: ({ children }: { children?: React.ReactNode }) => (
      <Box component="sub" sx={{ fontSize: '0.75em', verticalAlign: 'sub' }}>
        {children}
      </Box>
    ),
    mark: ({ children }: { children?: React.ReactNode }) => (
      <Box component="mark" sx={{ backgroundColor: 'warning.light', px: 0.25, borderRadius: 0.25 }}>
        {children}
      </Box>
    ),
    code({ inline, className, children, ...props }: CodeProps) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter style={prismTheme as any} language={match[1]} PreTag="div" {...props}>
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <Box
          component="code"
          className={className}
          sx={{ backgroundColor: 'grey.100', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace', fontSize: '0.9em' }}
          {...props}
        >
          {children}
        </Box>
      );
    },
    pre: ({ children }: { children?: React.ReactNode }) => {
      const getTextContent = (node: React.ReactNode): string => {
        if (typeof node === 'string') return node;
        if (Array.isArray(node)) return node.map(getTextContent).join('');
        if (React.isValidElement(node) && node.props?.children) {
          return getTextContent(node.props.children);
        }
        return '';
      };
      const textContent = getTextContent(children);
      const { isTable, headers, rows } = parseAsciiTable(textContent);

      if (isTable && headers.length > 0) {
        return (
          <Box sx={{ overflowX: 'auto', my: 2 }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', display: 'table', minWidth: '400px' }}>
              <Box component="thead" sx={{ display: 'table-header-group', backgroundColor: 'grey.100' }}>
                <Box component="tr" sx={{ display: 'table-row' }}>
                  {headers.map((header, i) => (
                    <Box
                      key={i}
                      component="th"
                      sx={{ border: '1px solid #e5e7eb', p: '8px', textAlign: 'left', fontWeight: 600, display: 'table-cell' }}
                    >
                      {header}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody" sx={{ display: 'table-row-group' }}>
                {rows.map((row, i) => (
                  <Box key={i} component="tr" sx={{ display: 'table-row' }}>
                    {row.map((cell, j) => (
                      <Box
                        key={j}
                        component="td"
                        sx={{ border: '1px solid #e5e7eb', p: '8px', textAlign: 'left', display: 'table-cell' }}
                      >
                        {cell}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );
      }

      return (
        <Box component="pre" sx={defaultPreSx}>
          {children}
        </Box>
      );
    },
  };
}
